import type { RoleKey } from '@k2/domain/rights'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useInvitationsResource, useTeamResource } from '../resources'

export type TeamMember = {
  membershipId: string
  userId: string
  email: string
  name: string
  avatarInitials: string
  picture?: string
  roleKeys: RoleKey[]
  brandIds: string[]
  joinedAt: Date
}

export type PendingInvitation = {
  invitationId: string
  email: string
  roleKeys: RoleKey[]
  brandIds: string[]
  invitedAt: Date
  expiresAt: Date
}

export type Team = {
  verified: boolean
  canInvite: boolean
  canManage: boolean
  brands: { id: string; name: string }[]
  members: TeamMember[]
  invitations: PendingInvitation[]
}

// What the routes refuse with; `failed` is the catch-all, so an unnamed error still reaches the surface (NFR-A1).
export type TeamRefusal =
  | 'unverified'
  | 'already_member'
  | 'already_invited'
  | 'unknown_brand'
  | 'unknown_role'
  | 'not_found'
  | 'last_manager'
  | 'failed'

export const useTeamStore = defineStore('team', () => {
  const { getTeam, setTeamInvitation, delTeamInvitation, setTeamMemberRoles, setTeamMemberBrands, delTeamMember } = useTeamResource()
  const { setInvitationAcceptance } = useInvitationsResource()
  const team = ref<Team>()
  const pending = ref(false)
  const refusal = ref<TeamRefusal>()

  const load = async (organizationId: string) => {
    const { data } = await getTeam(organizationId)
    team.value = data ?? undefined
  }

  const act = async (organizationId: string, run: () => Promise<{ error: unknown } | { error: null }>) => {
    pending.value = true
    refusal.value = undefined
    try {
      const { error } = await run()
      if (error) {
        refusal.value = (error as { value?: { error?: TeamRefusal } }).value?.error ?? 'failed'
        return false
      }
      await load(organizationId)
      return true
    } finally {
      pending.value = false
    }
  }

  const invite = (organizationId: string, email: string, roleKeys: RoleKey[], brandIds: string[]) =>
    act(organizationId, () => setTeamInvitation(organizationId, { email, roleKeys, brandIds }))

  const revoke = (organizationId: string, invitationId: string) =>
    act(organizationId, () => delTeamInvitation(organizationId, invitationId))

  const setRoles = (organizationId: string, membershipId: string, roleKeys: RoleKey[]) =>
    act(organizationId, () => setTeamMemberRoles(organizationId, membershipId, { roleKeys }))

  const setBrands = (organizationId: string, membershipId: string, brandIds: string[]) =>
    act(organizationId, () => setTeamMemberBrands(organizationId, membershipId, { brandIds }))

  const remove = (organizationId: string, membershipId: string) => act(organizationId, () => delTeamMember(organizationId, membershipId))

  // FR-A16: no token is the ordinary case, and a token only narrows to the one they clicked.
  const accept = async (token?: string) => {
    const { data } = await setInvitationAcceptance({ token })
    return data ?? []
  }

  // Store state, not module state a second SSR request would inherit.
  const checked = ref(false)

  const acceptOnce = async () => {
    if (checked.value) return []
    checked.value = true
    return accept()
  }

  return { team, pending, refusal, load, invite, revoke, setRoles, setBrands, remove, accept, acceptOnce }
})
